import type { ReactNode } from "react";
import { cn } from "../../lib/utils";

type AvatarSize = "sm" | "md" | "lg" | "hero" | "xl" | "2xl";

/** How the image fills its frame: `cover` crops to fill (profile pictures),
 * `scale-down` contains without cropping (room logos). */
type AvatarImageFit = "cover" | "scale-down";

type AvatarVariant =
  | { variant: "initials"; initials: string }
  | { variant: "image"; src: string; alt: string; imageFit: AvatarImageFit }
  | { variant: "icon"; icon: ReactNode };

const imageFitClasses: Record<AvatarImageFit, string> = {
  cover: "object-cover",
  "scale-down": "object-scale-down",
};

type AvatarProps = AvatarVariant & {
  size?: AvatarSize;
  className?: string;
  /** Corner overlay (e.g. edit action, status). Straddles the bottom-right. */
  badge: ReactNode | null;
};

const sizeClasses: Record<AvatarSize, string> = {
  sm: "size-6 rounded-md",
  md: "size-8 rounded-lg",
  lg: "size-[52px] rounded-xl",
  hero: "size-16 rounded-2xl",
  xl: "size-32 rounded-2xl",
  "2xl": "size-52 rounded-3xl",
};

const initialsTextClasses: Record<AvatarSize, string> = {
  sm: "text-caption font-bold",
  md: "text-sm font-semibold",
  lg: "text-xl leading-[52px] font-semibold",
  hero: "text-2xl font-semibold",
  xl: "text-4xl font-semibold",
  "2xl": "text-6xl font-semibold",
};

const iconSizeClasses: Record<AvatarSize, string> = {
  sm: "size-4",
  md: "size-5",
  lg: "size-6",
  hero: "size-8",
  xl: "size-12",
  "2xl": "size-20",
};

function Avatar(props: AvatarProps): React.JSX.Element {
  const { size = "lg", className, badge } = props;

  return (
    <div className="relative inline-flex shrink-0">
      <div
        className={cn(
          "flex shrink-0 items-center justify-center overflow-clip text-ds-slate-500",
          sizeClasses[size],
          props.variant === "image" ? "bg-ds-white" : "bg-ds-slate-200",
          className
        )}
      >
        {props.variant === "initials" && (
          <span
            className={cn(
              "text-center font-dm-sans font-semibold",
              initialsTextClasses[size]
            )}
          >
            {props.initials}
          </span>
        )}
        {props.variant === "image" && (
          <img
            src={props.src}
            alt={props.alt}
            className={cn("size-full", imageFitClasses[props.imageFit])}
          />
        )}
        {props.variant === "icon" && (
          <span
            className={cn(
              "flex items-center justify-center",
              iconSizeClasses[size]
            )}
          >
            {props.icon}
          </span>
        )}
      </div>

      {badge !== null && (
        <span className="absolute bottom-0 right-0 flex translate-1/4  items-center justify-center">
          {badge}
        </span>
      )}
    </div>
  );
}

export { Avatar, type AvatarProps, type AvatarSize, type AvatarImageFit };
